import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Stack
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Description as DocumentsIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AccessTime as PunchInIcon,
  DateRange as DateRangeIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  School as SchoolIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Time period options for charts
const TIME_PERIODS = [
  { value: '1w', label: 'Last 1 Week', days: 7 },
  { value: '1m', label: 'Last 1 Month', days: 30 },
  { value: '6m', label: 'Last 6 Months', days: 180 },
  { value: '1y', label: 'Last 1 Year', days: 365 }
];

const LS_DASHLETS = 'erp4c.dashboard.dashlets.v1';

const DASHLET_CATALOG = [
  { id: 'contacts', label: 'Total Contacts', group: 'stats', defaultOn: true },
  { id: 'accounts', label: 'Active Accounts', group: 'stats', defaultOn: true },
  { id: 'jobs', label: 'Active Jobs', group: 'stats', defaultOn: true },
  { id: 'documents', label: 'Total Documents', group: 'stats', defaultOn: true },
  { id: 'course_catalog', label: 'Course Catalog', group: 'stats', defaultOn: true },
  { id: 'jobs_priority', label: 'Jobs by Priority', group: 'panels', defaultOn: true },
  { id: 'submissions', label: 'Submissions Pipeline', group: 'panels', defaultOn: true },
  { id: 'critical_jobs', label: 'Critical Jobs', group: 'panels', defaultOn: true },
  { id: 'recent_submissions', label: 'Recent Submissions', group: 'panels', defaultOn: true },
  { id: 'weekly_activity', label: 'Weekly Activity', group: 'panels', defaultOn: true }
];

const STAT_CARD_THEMES = {
  contacts: { bg: '#EEF2FF', border: '#6366F1', color: '#4F46E5' },
  accounts: { bg: '#FDF2F8', border: '#EC4899', color: '#DB2777' },
  jobs: { bg: '#ECFDF5', border: '#10B981', color: '#059669' },
  documents: { bg: '#FFFBEB', border: '#F59E0B', color: '#D97706' },
  course_catalog: { bg: '#E0F2FE', border: '#0EA5E9', color: '#0369A1' }
};

const PANEL_CARD_SX = {
  height: '100%',
  border: '2px solid',
  borderColor: '#CBD5E1',
  bgcolor: '#F8FAFC',
  borderRadius: 2,
  boxShadow: '0 2px 8px rgba(15,23,42,0.06)'
};

const loadDashletPrefs = () => {
  try {
    const raw = localStorage.getItem(LS_DASHLETS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.enabled?.length) return parsed;
    }
  } catch {
    /* ignore */
  }
  return {
    enabled: DASHLET_CATALOG.filter((d) => d.defaultOn).map((d) => d.id),
    order: DASHLET_CATALOG.map((d) => d.id)
  };
};

const StatCard = ({ title, value, icon, themeKey, trend, onClick }) => {
  const theme = STAT_CARD_THEMES[themeKey] || STAT_CARD_THEMES.contacts;
  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        bgcolor: theme.bg,
        border: '2px solid',
        borderColor: theme.border,
        borderRadius: 2,
        boxShadow: '0 2px 10px rgba(15,23,42,0.06)',
        '&:hover': onClick
          ? {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0, 0, 0, 0.12)'
            }
          : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" sx={{ color: theme.color, fontWeight: 600 }} gutterBottom>
              {title}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: theme.color }}>
              {value}
            </Typography>
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="caption" color="success.main">
                  {trend}
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              backgroundColor: `${theme.color}22`,
              color: theme.color
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [punchInMessage, setPunchInMessage] = useState(null);
  const [idlePolicySummary, setIdlePolicySummary] = useState(null);
  const [idlePolicyExpanded, setIdlePolicyExpanded] = useState(false);
  const [submissionPeriod, setSubmissionPeriod] = useState('1m'); // Default to last 1 month
  const [dashPrefs, setDashPrefs] = useState(loadDashletPrefs);
  const [dashletsOpen, setDashletsOpen] = useState(false);
  const [draftEnabled, setDraftEnabled] = useState([]);
  const [dragId, setDragId] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const isDashOn = (id) => (dashPrefs.enabled || []).includes(id);

  const saveDashPrefs = (next) => {
    setDashPrefs(next);
    try {
      localStorage.setItem(LS_DASHLETS, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  const openDashlets = () => {
    setDraftEnabled([...(dashPrefs.enabled || [])]);
    setDashletsOpen(true);
  };

  const applyDashlets = () => {
    const order = [
      ...DASHLET_CATALOG.map((d) => d.id).filter((id) => draftEnabled.includes(id)),
      ...(dashPrefs.order || []).filter((id) => draftEnabled.includes(id) && !DASHLET_CATALOG.some((d) => d.id === id))
    ];
    const uniqueOrder = [...new Set([...order, ...draftEnabled])];
    saveDashPrefs({ enabled: draftEnabled, order: uniqueOrder });
    setDashletsOpen(false);
  };

  const moveDashlet = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const order = [...(dashPrefs.order || DASHLET_CATALOG.map((d) => d.id))];
    const fi = order.indexOf(fromId);
    const ti = order.indexOf(toId);
    if (fi < 0 || ti < 0) return;
    order.splice(fi, 1);
    order.splice(ti, 0, fromId);
    saveDashPrefs({ ...dashPrefs, order });
  };

  const STAT_IDS = ['contacts', 'accounts', 'jobs', 'documents', 'course_catalog'];
  const PANEL_IDS = ['jobs_priority', 'submissions', 'critical_jobs', 'recent_submissions', 'weekly_activity'];

  const orderedStatIds = (dashPrefs.order || []).filter((id) => STAT_IDS.includes(id) && isDashOn(id));
  STAT_IDS.forEach((id) => {
    if (isDashOn(id) && !orderedStatIds.includes(id)) orderedStatIds.push(id);
  });

  const orderedPanelIds = (dashPrefs.order || []).filter((id) => PANEL_IDS.includes(id) && isDashOn(id));
  PANEL_IDS.forEach((id) => {
    if (isDashOn(id) && !orderedPanelIds.includes(id)) orderedPanelIds.push(id);
  });

  const panelDragProps = (id) => ({
    draggable: true,
    onDragStart: () => setDragId(id),
    onDragOver: (e) => e.preventDefault(),
    onDrop: () => {
      moveDashlet(dragId, id);
      setDragId(null);
    }
  });

  const fetchDashboard = useCallback(async (period = submissionPeriod) => {
    setLoading(true);
    try {
      const response = await api.get('/dashboard', { params: { period } });
      setStats(response.data.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [submissionPeriod]);

  useEffect(() => {
    fetchDashboard();

    (async () => {
      try {
        const r = await api.get('/punchcard/idle-policy-summary');
        setIdlePolicySummary(r.data?.data || null);
      } catch {
        setIdlePolicySummary(null);
      }
    })();

    // Check for punch-in message from login
    const storedMessage = sessionStorage.getItem('punchInMessage');
    if (storedMessage) {
      setPunchInMessage(storedMessage);
      sessionStorage.removeItem('punchInMessage');
    }
  }, []);

  // Handle time period change
  const handlePeriodChange = (event) => {
    const newPeriod = event.target.value;
    setSubmissionPeriod(newPeriod);
    fetchDashboard(newPeriod);
  };

  const priorityColors = {
    critical: '#ef4444',
    high: '#f59e0b',
    medium: '#3b82f6',
    low: '#10b981'
  };

  const statusColors = {
    active: '#10b981',
    on_hold: '#f59e0b',
    filled: '#3b82f6',
    closed: '#6b7280'
  };

  const jobsPriorityData = stats ? {
    labels: stats.jobs.byPriority.map(p => p.priority.charAt(0).toUpperCase() + p.priority.slice(1)),
    datasets: [{
      data: stats.jobs.byPriority.map(p => parseInt(p.count)),
      backgroundColor: stats.jobs.byPriority.map(p => priorityColors[p.priority]),
      borderWidth: 0
    }]
  } : null;

  const submissionsData = stats ? {
    labels: stats.submissions.byStatus.slice(0, 6).map(s => s.status.replace('_', ' ').toUpperCase()),
    datasets: [{
      label: 'Submissions',
      data: stats.submissions.byStatus.slice(0, 6).map(s => parseInt(s.count)),
      backgroundColor: '#6366f1',
      borderRadius: 8
    }]
  } : null;

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>Dashboard</Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={300} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={300} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Welcome back, {user?.first_name}!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Here's what's happening with your business today. Customize with + Add Dashlets.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openDashlets}>
            + Add Dashlets
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchDashboard}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {idlePolicySummary?.summary?.periods?.length ? (
        <Box
          sx={{
            mb: 3,
            borderRadius: 2,
            border: (t) => `1px solid ${alpha(t.palette.error.main, 0.45)}`,
            bgcolor: (t) => alpha(t.palette.error.main, 0.1),
            boxShadow: (t) => `0 0 0 1px ${alpha(t.palette.error.main, 0.08)}`,
            overflow: 'hidden',
          }}
        >
          <Box
            onClick={() => setIdlePolicyExpanded((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIdlePolicyExpanded((v) => !v);
              }
            }}
            role="button"
            tabIndex={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.06) },
            }}
          >
            <IconButton
              size="small"
              aria-expanded={idlePolicyExpanded}
              aria-label={idlePolicyExpanded ? 'Collapse idle policy details' : 'Expand idle policy details'}
              onClick={(e) => {
                e.stopPropagation();
                setIdlePolicyExpanded((v) => !v);
              }}
              sx={{ color: 'error.main' }}
            >
              {idlePolicyExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
            </IconButton>
            <Typography
              component="div"
              variant="body2"
              sx={{
                flex: 1,
                color: 'error.dark',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                lineHeight: 1.35,
              }}
            >
              <Box component="span" aria-hidden sx={{ fontSize: '1.1rem' }}>
                👉
              </Box>
              <span>Employee Idle Time Allowed in the System (DWMQY)</span>
              <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                {idlePolicyExpanded ? '— click to hide details' : '— click here to see in full'}
              </Typography>
            </Typography>
          </Box>
          {idlePolicyExpanded ? (
            <Box sx={{ px: 2, pb: 2, pt: 0.5, borderTop: (t) => `1px solid ${alpha(t.palette.error.main, 0.2)}` }}>
              <Box
                sx={{
                  mt: 1,
                  display: 'grid',
                  gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
                  gap: 1,
                  fontSize: '0.85rem',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.dark' }}>
                  Format
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.dark' }}>
                  Idle minutes
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.dark' }}>
                  Allowed / Used
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'error.dark' }}>
                  Status
                </Typography>
                {idlePolicySummary.summary.periods.map((p) => (
                  <React.Fragment key={p.key}>
                    <Typography variant="body2" color="error.dark">
                      {p.label}
                    </Typography>
                    <Typography variant="body2" color="error.dark">
                      {p.idleMinutes}
                    </Typography>
                    <Typography variant="body2" color="error.dark">
                      {p.allowedTimes} / {p.usedTimes}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: p.isExceeded ? 700 : 500, color: p.isExceeded ? 'error.main' : 'error.dark' }}
                    >
                      {!p.enabled ? 'Disabled' : p.isExceeded ? `Exceeded by ${p.exceededTimes}` : 'Within limit'}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
              <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                Idle counts within configured limits have no impact on performance. Anything beyond limit is highlighted
                and can potentially affect performance review.
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {orderedStatIds.map((id) => {
          const commonDrag = {
            draggable: true,
            onDragStart: () => setDragId(id),
            onDragOver: (e) => e.preventDefault(),
            onDrop: () => {
              moveDashlet(dragId, id);
              setDragId(null);
            }
          };
          if (id === 'contacts') {
            return (
              <Grid item xs={12} sm={6} md={3} key={id} {...commonDrag}>
                <Box sx={{ position: 'relative' }}>
                  <DragIcon sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, opacity: 0.35, fontSize: 18 }} />
                  <StatCard
                    title="Total Contacts"
                    value={stats?.overview.contacts || 0}
                    icon={<PeopleIcon />}
                    themeKey="contacts"
                    onClick={() => navigate('/contacts')}
                  />
                </Box>
              </Grid>
            );
          }
          if (id === 'accounts') {
            return (
              <Grid item xs={12} sm={6} md={3} key={id} {...commonDrag}>
                <Box sx={{ position: 'relative' }}>
                  <DragIcon sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, opacity: 0.35, fontSize: 18 }} />
                  <StatCard
                    title="Active Accounts"
                    value={stats?.overview.accounts || 0}
                    icon={<BusinessIcon />}
                    themeKey="accounts"
                    onClick={() => navigate('/accounts')}
                  />
                </Box>
              </Grid>
            );
          }
          if (id === 'jobs') {
            return (
              <Grid item xs={12} sm={6} md={3} key={id} {...commonDrag}>
                <Box sx={{ position: 'relative' }}>
                  <DragIcon sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, opacity: 0.35, fontSize: 18 }} />
                  <StatCard
                    title="Active Jobs"
                    value={stats?.overview.activeJobs || 0}
                    icon={<WorkIcon />}
                    themeKey="jobs"
                    onClick={() => navigate('/jobs')}
                  />
                </Box>
              </Grid>
            );
          }
          if (id === 'documents') {
            return (
              <Grid item xs={12} sm={6} md={3} key={id} {...commonDrag}>
                <Box sx={{ position: 'relative' }}>
                  <DragIcon sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, opacity: 0.35, fontSize: 18 }} />
                  <StatCard
                    title="Total Documents"
                    value={stats?.overview.documents || 0}
                    icon={<DocumentsIcon />}
                    themeKey="documents"
                    onClick={() => navigate('/documents')}
                  />
                </Box>
              </Grid>
            );
          }
          if (id === 'course_catalog') {
            return (
              <Grid item xs={12} sm={6} md={3} key={id} {...commonDrag}>
                <Box sx={{ position: 'relative' }}>
                  <DragIcon sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, opacity: 0.35, fontSize: 18 }} />
                  <StatCard
                    title="Course Catalog"
                    value="Browse"
                    icon={<SchoolIcon />}
                    themeKey="course_catalog"
                    onClick={() => navigate('/training/catalog')}
                  />
                </Box>
              </Grid>
            );
          }
          return null;
        })}
      </Grid>

      {/* Charts and Lists */}
      <Grid container spacing={3}>
        {orderedPanelIds.map((panelId) => {
          if (panelId === 'jobs_priority') {
            return (
              <Grid item xs={12} md={6} key={panelId} {...panelDragProps(panelId)}>
                <Card sx={{ ...PANEL_CARD_SX, bgcolor: '#EEF2FF', borderColor: '#818CF8' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                      <DragIcon sx={{ opacity: 0.35, fontSize: 18 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Jobs by Priority
                      </Typography>
                    </Box>
                    {jobsPriorityData && (
                      <Box sx={{ height: 250, display: 'flex', justifyContent: 'center' }}>
                        <Doughnut
                          data={jobsPriorityData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: {
                                position: 'bottom',
                                labels: { color: '#a1a1aa' }
                              }
                            }
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          }

          if (panelId === 'submissions') {
            return (
              <Grid item xs={12} md={6} key={panelId} {...panelDragProps(panelId)}>
                <Card sx={{ ...PANEL_CARD_SX, bgcolor: '#FFF7ED', borderColor: '#FB923C' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <DragIcon sx={{ opacity: 0.35, fontSize: 18 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Submissions Pipeline
                        </Typography>
                      </Box>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                          value={submissionPeriod}
                          onChange={handlePeriodChange}
                          displayEmpty
                          sx={{
                            fontSize: '0.85rem',
                            '& .MuiSelect-select': { py: 0.75 }
                          }}
                          startAdornment={<DateRangeIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />}
                        >
                          {TIME_PERIODS.map((period) => (
                            <MenuItem key={period.value} value={period.value}>
                              {period.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    {submissionsData && (
                      <Box sx={{ height: 250 }}>
                        <Bar
                          data={submissionsData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { display: false },
                              title: { display: false }
                            },
                            scales: {
                              x: {
                                grid: { display: false },
                                ticks: {
                                  color: '#a1a1aa',
                                  font: { size: 10 }
                                },
                                title: {
                                  display: true,
                                  text: 'Status',
                                  color: '#a1a1aa',
                                  font: { size: 11, weight: 500 }
                                }
                              },
                              y: {
                                grid: { color: 'rgba(255,255,255,0.05)' },
                                ticks: {
                                  color: '#a1a1aa',
                                  stepSize: 1,
                                  precision: 0,
                                  callback: function (value) {
                                    if (Number.isInteger(value)) {
                                      return value;
                                    }
                                    return null;
                                  }
                                },
                                beginAtZero: true,
                                title: {
                                  display: true,
                                  text: 'Number of Submissions',
                                  color: '#a1a1aa',
                                  font: { size: 11, weight: 500 }
                                }
                              }
                            }
                          }}
                        />
                      </Box>
                    )}
                    {(!submissionsData || submissionsData.datasets[0].data.every((v) => v === 0)) && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No submissions in this period
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          }

          if (panelId === 'critical_jobs') {
            return (
              <Grid item xs={12} md={6} key={panelId} {...panelDragProps(panelId)}>
                <Card sx={{ ...PANEL_CARD_SX, bgcolor: '#FEF2F2', borderColor: '#F87171' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <DragIcon sx={{ opacity: 0.35, fontSize: 18 }} />
                      <WarningIcon sx={{ color: 'error.main' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Critical Jobs
                      </Typography>
                    </Box>
                    <List disablePadding>
                      {stats?.jobs.criticalJobs.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                          No critical jobs at the moment 🎉
                        </Typography>
                      ) : (
                        stats?.jobs.criticalJobs.map((job, index) => (
                          <ListItem
                            key={job.id}
                            sx={{
                              px: 0,
                              borderBottom:
                                index < stats.jobs.criticalJobs.length - 1
                                  ? '1px solid rgba(15,23,42,0.08)'
                                  : 'none',
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'rgba(15,23,42,0.03)' }
                            }}
                            onClick={() => navigate(`/jobs/${job.id}`)}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'error.main', width: 40, height: 40 }}>
                                <WorkIcon fontSize="small" />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={job.title}
                              secondary={`${job.client?.company_name || 'N/A'} • ${job.location || 'Remote'}`}
                              primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                            />
                            <Chip
                              label={`${job.positions_available} positions`}
                              size="small"
                              sx={{ bgcolor: 'rgba(239, 68, 68, 0.15)', color: '#f87171' }}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            );
          }

          if (panelId === 'recent_submissions') {
            return (
              <Grid item xs={12} md={6} key={panelId} {...panelDragProps(panelId)}>
                <Card sx={{ ...PANEL_CARD_SX, bgcolor: '#F5F3FF', borderColor: '#A78BFA' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <DragIcon sx={{ opacity: 0.35, fontSize: 18 }} />
                      <ScheduleIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Recent Submissions
                      </Typography>
                    </Box>
                    <List disablePadding>
                      {stats?.submissions.recent.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                          No recent submissions
                        </Typography>
                      ) : (
                        stats?.submissions.recent.map((submission, index) => (
                          <ListItem
                            key={submission.id}
                            sx={{
                              px: 0,
                              borderBottom:
                                index < stats.submissions.recent.length - 1
                                  ? '1px solid rgba(15,23,42,0.08)'
                                  : 'none'
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                                {submission.candidate?.first_name?.[0]}
                                {submission.candidate?.last_name?.[0]}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={`${submission.candidate?.first_name} ${submission.candidate?.last_name}`}
                              secondary={submission.JobRequirement?.title}
                              primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                            />
                            <Chip
                              label={submission.status.replace('_', ' ')}
                              size="small"
                              sx={{
                                bgcolor:
                                  submission.status === 'submitted'
                                    ? 'rgba(99, 102, 241, 0.15)'
                                    : 'rgba(16, 185, 129, 0.15)',
                                color: submission.status === 'submitted' ? '#818cf8' : '#34d399'
                              }}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            );
          }

          if (panelId === 'weekly_activity') {
            return (
              <Grid item xs={12} key={panelId} {...panelDragProps(panelId)}>
                <Card sx={{ ...PANEL_CARD_SX, bgcolor: '#F0FDF4', borderColor: '#4ADE80' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
                      <DragIcon sx={{ opacity: 0.35, fontSize: 18 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Weekly Activity
                      </Typography>
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {stats?.recentActivity.newContacts || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            New Contacts
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                            {stats?.recentActivity.newJobs || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            New Jobs
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                            {stats?.recentActivity.newSubmissions || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            New Submissions
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            );
          }

          return null;
        })}
      </Grid>

      <Dialog open={dashletsOpen} onClose={() => setDashletsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>+ Add Dashlets</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose what appears on your dashboard. Drag stat cards on the board to rearrange. Currently on:{' '}
            <strong>{(dashPrefs.enabled || []).length}</strong> · Available:{' '}
            <strong>{DASHLET_CATALOG.length}</strong>
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Currently shown
          </Typography>
          {DASHLET_CATALOG.filter((d) => draftEnabled.includes(d.id)).map((d) => (
            <FormControlLabel
              key={`on-${d.id}`}
              control={
                <Checkbox
                  checked
                  onChange={() => setDraftEnabled((prev) => prev.filter((x) => x !== d.id))}
                />
              }
              label={`${d.label} (${d.group})`}
            />
          ))}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            More you can add
          </Typography>
          {DASHLET_CATALOG.filter((d) => !draftEnabled.includes(d.id)).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              All dashlets are already on your dashboard.
            </Typography>
          ) : (
            DASHLET_CATALOG.filter((d) => !draftEnabled.includes(d.id)).map((d) => (
              <FormControlLabel
                key={`off-${d.id}`}
                control={
                  <Checkbox
                    checked={false}
                    onChange={() => setDraftEnabled((prev) => [...prev, d.id])}
                  />
                }
                label={`${d.label} (${d.group})`}
              />
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDashletsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyDashlets}>
            Save to dashboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Punch In Message Snackbar */}
      <Snackbar
        open={Boolean(punchInMessage)}
        autoHideDuration={10000}
        onClose={() => setPunchInMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setPunchInMessage(null)}
          severity="info"
          variant="filled"
          icon={<PunchInIcon />}
          sx={{ 
            width: '100%',
            maxWidth: 600,
            '& .MuiAlert-message': {
              fontSize: '0.9rem'
            }
          }}
        >
          {punchInMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
